import { h, Fragment } from 'preact';
import { useState } from 'preact/hooks';
import { OrthomolIcon } from '~/components-common/OrthomolIcon';
import { useShopware } from '../shopware/ShopwareContext';
import { URLS } from '../Urls';
import type { Order, OrderLineItem, Document } from '../shopware/shopwareApi';
import { formatPrice, formatDate } from '../shopware/shopwareUtils';
import { OrderLineItem as OrderLineItemComponent } from './OrderLineItem';
import { useOrders } from './OrdersContext';

interface OrdersListItemProps {
  order: Order;
  isExpanded: boolean;
  onToggleDetails: (orderNumber: string) => void;
}

export const OrdersListItem = ({ order, isExpanded, onToggleDetails }: OrdersListItemProps) => {
  const { addMultipleProducts, triggerDocumentDownload } = useShopware();
  const { getOrderTotalPoints, isLoadingDetails, hasDetailsError } = useOrders();
  const [isReordering, setIsReordering] = useState(false);
  const [hasError, setHasError] = useState(false);

  // Get shipping status from deliveries or stateMachineState
  const getShippingStatus = (): string => {
    // Check if there's a primary delivery with state
    if (order.primaryOrderDelivery?.stateMachineState) {
      return order.primaryOrderDelivery.stateMachineState.name || 'Unbekannt';
    }
    // Fallback to first delivery's state
    if (order.deliveries && order.deliveries.length > 0 && order.deliveries[0].stateMachineState) {
      return order.deliveries[0].stateMachineState.name || 'Unbekannt';
    }
    return 'Unbekannt';
  };

  // Get tracking number from deliveries
  const getTrackingNumber = (): string | null => {
    if (order.deliveries && order.deliveries.length > 0 && order.deliveries[0].trackingCodes) {
      const codes = order.deliveries[0].trackingCodes;
      return codes.length > 0 ? codes[0] : null;
    }
    return null;
  };

  // Get loyalty points from context (fetched from Pimcore PIM)
  const getLoyaltyPoints = (): number => {
    return getOrderTotalPoints(order);
  };

  // Get invoice document from order documents
  // Identify invoices by checking if document.config.name === 'invoice'
  const getInvoiceDocument = (): { id: string; deepLinkCode: string } | null => {
    if (order.documents && order.documents.length > 0) {
      // Find invoice document
      const invoice = order.documents.find((doc: Document) => (doc.config as any)?.name === 'invoice');
      if (invoice && invoice.id && invoice.deepLinkCode) {
        return {
          id: invoice.id,
          deepLinkCode: invoice.deepLinkCode,
        };
      }
    }
    return null;
  };

  // Handle invoice download
  const handleDownloadInvoice = async () => {
    const invoice = getInvoiceDocument();
    if (!invoice) return;

    try {
      await triggerDocumentDownload(invoice.id, invoice.deepLinkCode, `Rechnung-${orderNumber}.pdf`);
    } catch (error) {
      console.error('[OrdersListItem] Failed to download invoice:', error);
    }
  };

  // Get line items (products) - not included in the provided API response
  // Will need to fetch with associations or might be available in some responses
  const getLineItems = (): OrderLineItem[] => {
    return order.lineItems || [];
  };

  // Handle reorder - add all products from this order to cart
  const handleReorder = async () => {
    try {
      setIsReordering(true);
      setHasError(false);

      const lineItems = getLineItems();

      // Filter out promotions and map to addMultipleProducts format
      const products = lineItems
        .filter((item) => item.type !== 'promotion' && item.productId)
        .map((item) => ({
          productId: item.productId!,
          quantity: item.quantity || 1,
        }));

      if (products.length === 0) {
        setHasError(true);
        return;
      }

      // Add all products to cart in one API call
      await addMultipleProducts(products);

      // Navigate to cart page
      window.location.href = URLS.cart;
    } catch (error) {
      setHasError(true);
    } finally {
      setIsReordering(false);
    }
  };

  const orderNumber = order.orderNumber || '-';
  const orderDate = formatDate(order.orderDate);
  const amount = formatPrice(order.amountTotal);
  const shippingStatus = getShippingStatus();
  const trackingNumber = getTrackingNumber();
  const trackingUrl = trackingNumber
    ? `https://www.dhl.de/de/privatkunden/pakete-empfangen/verfolgen.html?piececode=${trackingNumber}`
    : undefined;
  const loyaltyPoints = getLoyaltyPoints();
  const invoiceDocument = getInvoiceDocument();
  const lineItems = getLineItems();

  // Separate products and promotions in a single pass
  const { products, promotions } = lineItems.reduce(
    (acc, item) => {
      if (item.type === 'promotion') {
        acc.promotions.push(item);
      } else {
        acc.products.push(item);
      }
      return acc;
    },
    { products: [] as OrderLineItem[], promotions: [] as OrderLineItem[] }
  );

  return (
    <Fragment>
      <tr className="bg-light mt-1 px-4 py-3 px-md-0 py-md-0 orders-table-row" data-order-id={orderNumber}>
        <td
          className="d-flex justify-content-between d-md-table-cell gap-2 ps-md-3 ps-xl-5 pe-md-3 py-md-3 text-nowrap"
          data-label="Bestellnummer"
        >
          <button
            className="orders-expand-button btn btn-link text-start p-0 text-decoration-none fw-bold text-primary"
            type="button"
            onClick={() => onToggleDetails(orderNumber)}
            aria-expanded={isExpanded}
          >
            {orderNumber}
            <span className="icon ms-2" style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
              <OrthomolIcon name="chevron-24" size="16" rotation={90} />
            </span>
          </button>
        </td>
        <td
          className="d-flex justify-content-between d-md-table-cell gap-2 p-md-3 mb-2 text-nowrap"
          data-label="Bestelldatum"
        >
          {orderDate}
        </td>
        <td
          className="d-flex justify-content-between d-md-table-cell gap-2 p-md-3 mb-2 text-nowrap"
          data-label="Betrag"
        >
          {amount} &euro;
        </td>
        <td
          className={
            'd-flex justify-content-between d-md-table-cell gap-2 p-md-3 mb-2 position-relative' +
            (trackingNumber ? ' pb-4' : '')
          }
          data-label="Versandstatus"
        >
          <div className="d-flex flex-column gap-1">
            <span>{shippingStatus}</span>
            {trackingNumber && trackingUrl && (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="orders-table-tracking-number small text-primary text-decoration-none"
              >
                Sendungsnummer: <span>{trackingNumber}</span>
              </a>
            )}
          </div>
        </td>
        <td
          className="d-flex justify-content-between d-md-table-cell gap-2 p-md-3 mb-2 text-nowrap text-md-center"
          data-label="Treuepunkte"
        >
          {isLoadingDetails ? (
            <span className="spinner-border spinner-border-sm" role="status" aria-label="Lade Treuepunkte..." />
          ) : hasDetailsError ? (
            <span className="text-muted" title="Treuepunkte konnten nicht geladen werden">
              –
            </span>
          ) : (
            loyaltyPoints.toLocaleString('de-DE')
          )}
        </td>
        <td
          className="d-flex justify-content-between d-md-table-cell gap-2 p-md-3 mb-2 text-nowrap text-md-center"
          data-label="Rechnung"
        >
          <button
            className="btn p-0 text-decoration-none text-primary border-0 bg-transparent download-invoice-button"
            disabled={!invoiceDocument}
            onClick={handleDownloadInvoice}
            aria-label="Rechnung herunterladen"
          >
            <span className="icon">
              <OrthomolIcon name="download-24" size="24" />
            </span>
          </button>
        </td>
      </tr>
      {isExpanded && (
        <tr>
          <td colSpan={6} className="p-0">
            <div className="order-details bg-white p-4 p-md-5">
              <h3 className="h5 mb-4">Bestellte Produkte</h3>
              {lineItems.length > 0 ? (
                <Fragment>
                  {/* Products Grid */}
                  {products.length > 0 && (
                    <div className="row g-4 mb-4">
                      {products.map((item) => (
                        <OrderLineItemComponent key={item.id} item={item} />
                      ))}
                    </div>
                  )}

                  {/* Promotions/Vouchers - Full Width */}
                  {promotions.map((item) => (
                    <OrderLineItemComponent key={item.id} item={item} />
                  ))}
                </Fragment>
              ) : (
                <p className="text-muted">Keine Produkte gefunden.</p>
              )}
              <div className="d-flex justify-content-end">
                <button
                  className="btn btn-primary"
                  onClick={handleReorder}
                  disabled={isReordering || lineItems.length === 0 || hasError}
                >
                  <span>
                    {hasError
                      ? 'Es ist ein Fehler aufgetreten'
                      : isReordering
                        ? 'Wird hinzugefügt...'
                        : 'Erneut bestellen'}
                  </span>
                  <span className="icon">
                    <OrthomolIcon name="arrow-24" size="24" />
                  </span>
                </button>
              </div>
            </div>
          </td>
        </tr>
      )}
    </Fragment>
  );
};
